import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { MagnifyingGlass, Archive, ArrowCounterClockwise, Trash, Warning, ShieldCheck } from "@phosphor-icons/react";

function DepRow({ label, n, danger }) {
  return <div className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
    <span className="text-slate-600">{label}</span>
    <span className={`font-mono font-bold ${danger && n > 0 ? "text-red-600" : "text-slate-800"}`}>{n}</span></div>;
}

export default function PolicyCleanup() {
  const { hasPerm, isSuperAdmin } = useAuth();
  const isSuper = isSuperAdmin;
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [target, setTarget] = useState(null);   // {doc, deps}
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get("/policies/manage/overview").then((r) => setDocs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openDelete = async (d) => {
    const r = await api.get(`/policies/manage/${d.id}/dependencies`);
    setTarget({ doc: d, deps: r.data }); setReason("");
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/policies/manage/${target.doc.id}`, { data: { reason } });
      toast.success("Deleted"); setTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const archive = async (d) => { await api.post(`/policies/manage/${d.id}/archive`); toast.success("Archived"); load(); };
  const restore = async (d) => { await api.post(`/policies/manage/${d.id}/restore`); toast.success("Restored"); load(); };

  const list = docs.filter((d) => (!q || `${d.title} ${d.ref}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="policy-cleanup">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 text-sm text-amber-900 flex items-start gap-2">
        <Warning size={18} weight="bold" className="mt-0.5 shrink-0" />
        <span>Deletion is blocked whenever staff acknowledgements depend on a document — archive it instead. Every action is recorded in the audit log. Removing a document from a book never deletes the document.</span>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="cleanup-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-8 h-9" />
        </div>
        {isSuper && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1"><ShieldCheck size={13} /> Super-admin</span>}
      </div>
      <div className="flex flex-col gap-1.5" data-testid="cleanup-list">
        {list.map((d) => (
          <div key={d.id} data-testid={`cleanup-${d.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{d.title}</div>
              <div className="text-[11px] text-slate-400 font-mono">{d.ref}</div>
            </div>
            {d.content_state !== "structured" && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{d.content_state === "empty" ? "needs content" : d.content_state === "attachment" ? "attachment" : "no version"}</span>}
            <Button size="sm" variant="outline" data-testid={`archive-${d.id}`} onClick={() => archive(d)} className="gap-1"><Archive size={13} /> Archive</Button>
            <Button size="sm" variant="outline" data-testid={`restore-${d.id}`} onClick={() => restore(d)} className="gap-1"><ArrowCounterClockwise size={13} /> Restore</Button>
            <Button size="sm" variant="outline" data-testid={`delete-${d.id}`} onClick={() => openDelete(d)} className="gap-1 text-red-600 border-red-200 hover:bg-red-50"><Trash size={13} /> Delete</Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No documents.</p>}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader><DialogTitle>Delete “{target?.doc.title}”?</DialogTitle>
            <DialogDescription>Review the exact records that depend on this document before deleting.</DialogDescription></DialogHeader>
          {target && (<>
            <div className="rounded-lg border border-slate-200 p-3">
              <DepRow label="Staff acknowledgements (protected)" n={target.deps.acknowledgements} danger />
              <DepRow label="Published revisions" n={target.deps.revisions_published} />
              <DepRow label="Working drafts" n={target.deps.revisions_working} />
              <DepRow label="Active assignments" n={target.deps.assignments} />
              <DepRow label="Book memberships" n={target.deps.collection_memberships} />
              <DepRow label="Section memberships" n={target.deps.section_memberships} />
              <DepRow label="Feedback items" n={target.deps.feedback} />
            </div>
            {target.deps.can_hard_delete ? (
              <Textarea data-testid="delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for deletion (recorded in audit)" className="min-h-[70px]" />
            ) : (
              <p className="text-sm text-red-600 font-semibold">Blocked: this document has staff acknowledgements and cannot be hard-deleted. Use Archive instead.</p>
            )}
          </>)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            {target?.deps.can_hard_delete
              ? <Button data-testid="delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete permanently</Button>
              : <Button data-testid="archive-instead" onClick={() => { archive(target.doc); setTarget(null); }} className="bg-slate-800 hover:bg-slate-900 gap-1"><Archive size={14} /> Archive instead</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
